/**
 * Task type icons (19 Oct 2026): a library to pick from in Settings → Task types, drawn as solid 16 px shapes in the
 * type's colour (as ClickUp's). A type stores the icon's key; older types that still hold a character (◆ ● ▲ ■ ⬢)
 * show it as before.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/** key → [label, path] (16 × 16, filled, even-odd for holes). */
export const TYPE_ICONS: Record<string, [string, string]> = {
  task: [
    'Task',
    'M8 1.5a6.5 6.5 0 1 1 0 13 6.5 6.5 0 0 1 0-13zm0 2a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9zM8 5.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5z',
  ],
  milestone: ['Milestone', 'M8 1l7 7-7 7-7-7zm0 2.8L3.8 8 8 12.2 12.2 8zM8 6l2 2-2 2-2-2z'],
  bug: [
    'Bug',
    'M5.5 4.3a2.5 2.5 0 0 1 5 0V5h-5zM4 6h8v3.5a4 4 0 0 1-8 0zM1.5 7h2v1.3h-2zM12.5 7h2v1.3h-2zM1.8 10.4h2.1v1.3H1.8zM12.1 10.4h2.1v1.3h-2.1zM2.3 3.2l1.9 1.6-.8.9-1.9-1.6zM13.7 3.2l-1.9 1.6.8.9 1.9-1.6z',
  ],
  bulb: [
    'Feature',
    'M8 1.3a4.6 4.6 0 0 0-2.7 8.4v1.9h5.4V9.7A4.6 4.6 0 0 0 8 1.3zM5.8 12.6h4.4v.9A1.6 1.6 0 0 1 8.6 15H7.4a1.6 1.6 0 0 1-1.6-1.5z',
  ],
  sparkle: [
    'Improvement',
    'M7 1l1.6 4.4L13 7l-4.4 1.6L7 13l-1.6-4.4L1 7l4.4-1.6zM12.5 9.5l.8 1.9 1.9.8-1.9.8-.8 1.9-.8-1.9-1.9-.8 1.9-.8z',
  ],
  wrench: [
    'Maintenance',
    'M11 1.3a3.6 3.6 0 0 0-3.4 4.8L1.8 11.9a1.5 1.5 0 0 0 2.1 2.1l5.8-5.8A3.6 3.6 0 0 0 14.6 5l-2.1 2.1-2.4-.6-.6-2.4L11.6 2z',
  ],
  epic: ['Epic', 'M1.5 1.5h5.5V7H1.5zM9 1.5h5.5V7H9zM1.5 9H7v5.5H1.5zM9 9h5.5v5.5H9z'],
  star: ['Objective', 'M8 1.2l2.1 4.3 4.7.6-3.4 3.3.8 4.7L8 11.9l-4.2 2.2.8-4.7-3.4-3.3 4.7-.6z'],
  trophy: [
    'Goal',
    'M4.2 1.5h7.6V3h2.6v1.8a3.3 3.3 0 0 1-3 3.3A4 4 0 0 1 9 10.3v1.4h2.2v2.8H4.8v-2.8H7v-1.4A4 4 0 0 1 4.6 8.1a3.3 3.3 0 0 1-3-3.3V3h2.6zM3 4.4v.4a1.8 1.8 0 0 0 1.3 1.7 4 4 0 0 1-.1-.9V4.4zM13 4.4h-1.2v1.2a4 4 0 0 1-.1.9A1.8 1.8 0 0 0 13 4.8z',
  ],
  flag: ['Flag', 'M2.8 1.5h1.5v13H2.8zM5.2 2h8.3l-2.1 3.3 2.1 3.2H5.2z'],
  rocket: [
    'Release',
    'M8 .8c2.6 1.6 3.9 4.2 3.6 7.8l1.6 1.5V13l-2.6-1.4H5.4L2.8 13v-2.9l1.6-1.5C4.1 5 5.4 2.4 8 .8zM8 4.6a1.4 1.4 0 1 0 0 2.8 1.4 1.4 0 0 0 0-2.8zM6.4 12.4h3.2L8 15.2z',
  ],
  shield: ['Security', 'M8 1l5.6 2.1v4.6c0 3.4-2.3 6.1-5.6 7.5-3.3-1.4-5.6-4.1-5.6-7.5V3.1z'],
  code: [
    'Internal',
    'M5.2 3.4 1 8l4.2 4.6 1-1L3 8l3.2-3.6zM10.8 3.4l-1 1L13 8l-3.2 3.6 1 1L15 8zM9.1 2h1.4L6.9 14H5.5z',
  ],
  doc: ['Document', 'M3.5 1.5h6l3 3v10h-9zM5.5 7h5v1.1h-5zM5.5 9.4h5v1.1h-5zM5.5 11.8h3.4v1.1H5.5z'],
  clipboard: [
    'Form response',
    'M5.5 1.3h5v1.5H13v12H3v-12h2.5zM6.6 2.4v1.4h2.8V2.4zM5.3 7h5.4v1.1H5.3zM5.3 9.5h5.4v1.1H5.3zM5.3 12h3.6v1.1H5.3z',
  ],
  calendar: ['Event', 'M2 3h12v11.5H2zM4.3 1.3h1.6V4H4.3zM10.1 1.3h1.6V4h-1.6zM3.4 6.6v6.5h9.2V6.6z'],
  chat: ['Feedback', 'M1.8 2.2h12.4v8.9H7.4l-3.6 3v-3h-2z'],
  user: ['Person', 'M8 1.3a3.2 3.2 0 1 1 0 6.4 3.2 3.2 0 0 1 0-6.4zM2.3 14.7c0-3.1 2.6-5.3 5.7-5.3s5.7 2.2 5.7 5.3z'],
  users: [
    'Team',
    'M5.8 2a2.7 2.7 0 1 1 0 5.4 2.7 2.7 0 0 1 0-5.4zM11 2.8a2.3 2.3 0 1 1 0 4.6 2.3 2.3 0 0 1 0-4.6zM.8 14c0-2.8 2.2-4.8 5-4.8s5 2 5 4.8zM11.6 14c0-1.6-.6-3-1.6-4a4.3 4.3 0 0 1 5.2 4z',
  ],
  key: [
    'Key result',
    'M10.5 1.3a4.2 4.2 0 1 1-2.7 7.4L6.4 10.1H5v1.5H3.5v1.5L2.4 14.2H1.3v-2.3l5.2-5.2a4.2 4.2 0 0 1 4-5.4zM11.2 3.4a1.4 1.4 0 1 0 0 2.8 1.4 1.4 0 0 0 0-2.8z',
  ],
  bolt: ['Quick win', 'M9.6.8 2.8 9h4.3l-1.1 6.2L13.2 7H8.8z'],
  heart: ['Customer', 'M8 14.3S1.3 10.3 1.3 5.6A3.4 3.4 0 0 1 8 4.2a3.4 3.4 0 0 1 6.7 1.4C14.7 10.3 8 14.3 8 14.3z'],
  target: [
    'Target',
    'M8 1a7 7 0 1 1 0 14A7 7 0 0 1 8 1zm0 2a5 5 0 1 0 0 10A5 5 0 0 0 8 3zm0 2a3 3 0 1 1 0 6 3 3 0 0 1 0-6zm0 2a1 1 0 1 0 0 2 1 1 0 0 0 0-2z',
  ],
  chart: ['Report', 'M1.5 13.5h13v1.2h-13zM2.6 8h2.6v5H2.6zM6.7 3.8h2.6V13H6.7zM10.8 6.2h2.6V13h-2.6z'],
  book: [
    'Knowledge',
    'M1.5 2.3c2.1 0 4.2.4 5.8 1.4v11c-1.6-1-3.7-1.4-5.8-1.4zM14.5 2.3c-2.1 0-4.2.4-5.8 1.4v11c1.6-1 3.7-1.4 5.8-1.4z',
  ],
  megaphone: [
    'Announcement',
    'M12.8 1.8v11.4L7.2 10.6H3.6A1.6 1.6 0 0 1 2 9V5.8a1.6 1.6 0 0 1 1.6-1.6h3.6zM4 11.4h2.6l1 3.4H5z',
  ],
  box: ['Initiative', 'M8 1l6.3 3.1v7.8L8 15l-6.3-3.1V4.1zM3.4 5.2v5.7L7.3 13V7.2zM12.6 5.2 8.7 7.2V13l3.9-2.1z'],
  layers: [
    'Project',
    'M8 1.3 15 4.9 8 8.5 1 4.9zM2.5 7.2 8 10l5.5-2.8L15 8l-7 3.6L1 8zM2.5 10.3 8 13.1l5.5-2.8L15 11.1l-7 3.6-7-3.6z',
  ],
  check: ['Done', 'M8 1a7 7 0 1 1 0 14A7 7 0 0 1 8 1zm3.3 4.2L7 9.5 4.8 7.3l-1.1 1.1L7 11.7l5.4-5.4z'],
  alert: ['Incident', 'M8 1.3 15.2 14H.8zM7.2 5.8h1.6v4.4H7.2zM7.2 11.2h1.6v1.6H7.2z'],
  video: ['Content', 'M1.3 3.6h9.4v8.8H1.3zM11.4 6.6l3.3-2v6.8l-3.3-2z'],
  palette: [
    'Design',
    'M8 1.3a6.7 6.7 0 0 0 0 13.4c1 0 1.6-.6 1.6-1.5 0-.9-.7-1.2-.7-2s.6-1.4 1.4-1.4h1.9a2.6 2.6 0 0 0 2.6-2.6C14.8 4 11.8 1.3 8 1.3zM4.6 6.3a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2zM6.8 3.4a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2zM10 3.6a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2z',
  ],
  // The first shapes, kept so the old types can keep them.
  diamond: ['Diamond', 'M8 1.5 14.5 8 8 14.5 1.5 8z'],
  circle: ['Circle', 'M8 2a6 6 0 1 1 0 12A6 6 0 0 1 8 2z'],
  triangle: ['Triangle', 'M8 2 14.8 13.8H1.2z'],
  square: ['Square', 'M2.5 2.5h11v11h-11z'],
  hexagon: ['Hexagon', 'M8 1.3l5.8 3.35v6.7L8 14.7l-5.8-3.35v-6.7z'],
};
export const TYPE_ICON_KEYS = Object.keys(TYPE_ICONS);

/** A type's icon: the library's drawing, or the character an older type holds. */
export function TypeGlyph({ icon, size = 14 }: { icon: string; size?: number }) {
  const def = TYPE_ICONS[icon];
  if (!def) return <>{icon}</>;
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" className="tftypesvg">
      <path fillRule="evenodd" d={def[1]} />
    </svg>
  );
}

/**
 * Pick a type's icon from the library. The grid is drawn over the page (fixed, beside its button), so a settings card
 * that clips what overflows it cannot cut it off; it opens upwards when there is no room below.
 */
export function IconPicker({
  value,
  color,
  onChange,
  label = 'Icon',
}: {
  value: string;
  color: string;
  onChange: (key: string) => void;
  label?: string;
}) {
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const close = () => setAt(null);
  useEffect(() => {
    if (!at) return;
    const away = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!btn.current?.contains(t) && !pop.current?.contains(t)) close();
    };
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('mousedown', away);
    window.addEventListener('keydown', esc);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('mousedown', away);
      window.removeEventListener('keydown', esc);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [at]);
  const toggle = () => {
    if (at) return close();
    const r = btn.current!.getBoundingClientRect();
    const W = 288;
    const H = 40 * Math.ceil(TYPE_ICON_KEYS.length / 7) + 20;
    const below = r.bottom + 6 + H <= window.innerHeight;
    setAt({
      top: below ? r.bottom + 6 : Math.max(8, r.top - 6 - H),
      left: Math.min(Math.max(8, r.left), window.innerWidth - W - 8),
    });
  };
  return (
    <>
      <button
        ref={btn}
        type="button"
        className="tficonpick-btn"
        style={{ color }}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={!!at}
        onClick={toggle}
      >
        <TypeGlyph icon={value} size={16} />
      </button>
      {at &&
        createPortal(
          <div
            ref={pop}
            className="tfpop tficonpick"
            role="dialog"
            aria-label={label}
            style={{ top: at.top, left: at.left }}
          >
            {TYPE_ICON_KEYS.map((k) => (
              <button
                key={k}
                type="button"
                className={'tficonpick-i' + (k === value ? ' on' : '')}
                style={{ color }}
                title={TYPE_ICONS[k]![0]}
                aria-label={TYPE_ICONS[k]![0]}
                onClick={() => {
                  onChange(k);
                  close();
                }}
              >
                <TypeGlyph icon={k} size={16} />
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}
